/**
 * The die line, and the same line grown outwards for bleed.
 *
 * A round medal insert is printed on a square sheet and cut out, so the ink
 * has to run past the circle - a fraction of a millimetre of drift in the
 * cutter would otherwise leave a white crescent down one side. What it must
 * not do is fill the square: the customer is designing a medal, and a square
 * of colour on screen is a picture of something they are not buying.
 *
 * So the background is the cut line, grown by the bleed. For a circle that is
 * exactly a larger circle; for any other die line it is the shape pushed out
 * from its middle, which is what bleed means on a shaped cut.
 */

/**
 * A cut path scaled out from the middle of the page by the bleed.
 *
 * The path arrives in page millimetres, its origin the page's top-left corner.
 * What comes back is in the same coordinates, so it runs from -bleed to the
 * page size plus bleed.
 *
 * @param cut   The die line: absolute moves, lines and curves, as the product gives it.
 * @param w     Page width in millimetres.
 * @param h     Page height in millimetres.
 * @param bleed How far the ink runs past the cut, in millimetres.
 */
export function bleedCut( cut: string, w: number, h: number, bleed: number ): string {
  if ( ! cut || bleed <= 0 || w <= 0 || h <= 0 ) {
    return cut;
  }

  const sx = ( w + bleed * 2 ) / w;
  const sy = ( h + bleed * 2 ) / h;
  const cx = w / 2;
  const cy = h / 2;

  return transformPath( cut, ( x, y ) => [ cx + ( x - cx ) * sx, cy + ( y - cy ) * sy ] );
}

/**
 * Every coordinate pair in a path, moved.
 *
 * Only the commands a die line uses - move, line, curve, close - because that
 * is all a die line is. Anything else is left exactly as it was rather than
 * being mangled into a shape nobody asked for.
 */
export function transformPath( path: string, move: ( x: number, y: number ) => [ number, number ] ): string {
  const tokens = path.match( /[A-Za-z]|-?\d*\.?\d+(?:e[-+]?\d+)?/gi );

  if ( ! tokens ) {
    return path;
  }

  const out: string[] = [];
  let command = '';
  let numbers: number[] = [];

  const flush = () => {
    if ( ! command ) {
      return;
    }

    // M, L and C are pairs all the way along; Z carries nothing.
    if ( numbers.length && numbers.length % 2 === 0 ) {
      const moved: string[] = [];

      for ( let i = 0; i < numbers.length; i += 2 ) {
        const [ x, y ] = move( numbers[ i ]!, numbers[ i + 1 ]! );

        moved.push( round( x ), round( y ) );
      }

      out.push( command, ...moved );
    } else {
      out.push( command, ...numbers.map( ( n ) => round( n ) ) );
    }

    numbers = [];
  };

  for ( const token of tokens ) {
    if ( /[A-Za-z]/.test( token ) ) {
      flush();
      command = token;
      continue;
    }

    numbers.push( parseFloat( token ) );
  }

  flush();

  return out.join( ' ' );
}

/** Four decimal places is a ten-thousandth of a millimetre, and keeps the file short. */
function round( value: number ): string {
  return String( Math.round( value * 10000 ) / 10000 );
}
